import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, PhoneIcon } from "@heroicons/react/24/outline";
import {
  COMPANY_PROFILE_CALL_FOCUS_EVENT,
  consumePendingCompanyProfileCallFocus,
  focusCompanyProfileCallRecording,
} from "../utils/companyProfileSearch.js";

const formatCallDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const companyValueFromFields = (fields = {}) => {
  const raw = fields.Company;
  if (raw == null || raw === "") return null;
  if (Array.isArray(raw)) return raw[0] || null;
  const text = String(raw).trim();
  return text || null;
};

const companyLabelFromFields = (fields = {}) => {
  if (fields._companyName) return String(fields._companyName).trim();
  const raw = fields.Company;
  if (Array.isArray(raw)) return raw[0] || "Unknown company";
  const text = String(raw || "").trim();
  return text || "Unknown company";
};

const recordingMatchesSelection = (fields, selectedValue, selectedLabel) => {
  if (!selectedValue) return false;
  const raw = fields?.Company;
  if (raw == null || raw === "") return false;
  if (Array.isArray(raw)) return raw.includes(selectedValue);
  const text = String(raw).trim();
  if (!text) return false;
  if (text === selectedValue) return true;
  if (selectedLabel && text.toLowerCase() === selectedLabel.toLowerCase()) return true;
  return false;
};

const humanizeLabel = (key) =>
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim();

/** Turn loose takeaway payloads into [{ topic, takeaway }]. */
const normalizeTakeaways = (value) => {
  if (!value) return [];

  let items = value;
  if (typeof items === "string") {
    const text = items.trim();
    if (!text) return [];
    try {
      items = JSON.parse(text);
    } catch {
      // Common shape: {"topic":"..."}, {"topic":"..."} (not wrapped in [])
      try {
        items = JSON.parse(`[${text}]`);
      } catch {
        return [];
      }
    }
  }

  if (!Array.isArray(items)) {
    if (typeof items === "object") {
      const nested =
        items.key_takeaways ||
        items.keyTakeaways ||
        items.takeaways ||
        items["Key Takeaways"] ||
        items["key takeaways"];
      if (nested) return normalizeTakeaways(nested);
    }
    return [];
  }

  return items
    .map((item) => {
      if (typeof item === "string") {
        const text = item.trim();
        return text ? { topic: null, takeaway: text } : null;
      }
      if (!item || typeof item !== "object") return null;
      const topic =
        item.topic || item.heading || item.title || item.name || null;
      const takeaway =
        item.key_takeaway ||
        item.keyTakeaway ||
        item.takeaway ||
        item.summary ||
        item.text ||
        item.description ||
        null;
      if (!topic && !takeaway) return null;
      return {
        topic: topic ? String(topic).trim() : null,
        takeaway: takeaway ? String(takeaway).trim() : null,
      };
    })
    .filter(Boolean);
};

const parseSummary = (summary) => {
  if (summary == null || summary === "") return { type: "empty", value: null };
  if (typeof summary === "object") {
    const takeaways = normalizeTakeaways(summary);
    if (takeaways.length > 0 && (Array.isArray(summary) || summary.key_takeaways || summary.keyTakeaways || summary.takeaways)) {
      return { type: "takeaways", value: takeaways, extras: null };
    }
    return { type: "json", value: summary };
  }

  const text = String(summary).trim();
  if (!text) return { type: "empty", value: null };

  // Direct takeaway list (array or comma-separated objects)
  const directTakeaways = normalizeTakeaways(text);
  if (directTakeaways.length > 0 && (text.startsWith("[") || text.startsWith("{"))) {
    // Prefer takeaways view when every parsed item looks like a takeaway object
    const looksLikeTakeawayList =
      text.includes("key_takeaway") ||
      text.includes("keyTakeaway") ||
      text.includes('"topic"');
    if (looksLikeTakeawayList) {
      return { type: "takeaways", value: directTakeaways, extras: null };
    }
  }

  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      const takeaways = normalizeTakeaways(
        parsed.key_takeaways ||
          parsed.keyTakeaways ||
          parsed.takeaways ||
          parsed["Key Takeaways"] ||
          (Array.isArray(parsed) ? parsed : null)
      );
      if (takeaways.length > 0) {
        const extras =
          !Array.isArray(parsed) && typeof parsed === "object"
            ? Object.fromEntries(
                Object.entries(parsed).filter(
                  ([key]) =>
                    ![
                      "key_takeaways",
                      "keyTakeaways",
                      "takeaways",
                      "Key Takeaways",
                      "key takeaways",
                    ].includes(key)
                )
              )
            : null;
        return {
          type: "takeaways",
          value: takeaways,
          extras: extras && Object.keys(extras).length > 0 ? extras : null,
        };
      }
      return { type: "json", value: parsed };
    }
  } catch {
    // plain text
  }

  // Sectioned plain text with a Key takeaways: blob of JSON objects
  const keyTakeawaysMatch = text.match(
    /key\s*takeaways?\s*:\s*([\s\S]+?)(?=\n\s*(?:outcome|next steps|overview|conversation starters|participant insights)\s*:|$)/i
  );
  if (keyTakeawaysMatch) {
    const takeaways = normalizeTakeaways(keyTakeawaysMatch[1].trim());
    if (takeaways.length > 0) {
      return { type: "takeaways", value: takeaways, extras: null, rawPrefix: text.slice(0, keyTakeawaysMatch.index).trim() };
    }
  }

  return { type: "text", value: text };
};

const TakeawaysList = ({ takeaways }) => (
  <ul className="space-y-2">
    {takeaways.map((item, index) => (
      <li
        key={`${item.topic || "takeaway"}-${index}`}
        className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
      >
        {item.topic && (
          <p className="text-xs font-semibold text-gray-900">{item.topic}</p>
        )}
        {item.takeaway && (
          <p
            className={`text-sm leading-snug text-gray-600 ${
              item.topic ? "mt-0.5" : ""
            }`}
          >
            {item.takeaway}
          </p>
        )}
      </li>
    ))}
  </ul>
);

const JsonFields = ({ value }) => {
  const entries = Object.entries(value || {});
  if (entries.length === 0) {
    return <p className="text-sm text-gray-400">Empty summary</p>;
  }

  return (
    <dl className="space-y-3">
      {entries.map(([key, fieldValue]) => {
        const nestedTakeaways = normalizeTakeaways(fieldValue);
        const isTakeawayField =
          nestedTakeaways.length > 0 &&
          /takeaway/i.test(key);

        return (
          <div key={key}>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {humanizeLabel(key)}
            </dt>
            <dd className="mt-1.5 text-sm text-gray-700 break-words">
              {isTakeawayField ? (
                <TakeawaysList takeaways={nestedTakeaways} />
              ) : typeof fieldValue === "string" || typeof fieldValue === "number" ? (
                <span className="whitespace-pre-wrap leading-relaxed">
                  {String(fieldValue)}
                </span>
              ) : Array.isArray(fieldValue) ? (
                nestedTakeaways.length > 0 ? (
                  <TakeawaysList takeaways={nestedTakeaways} />
                ) : (
                  <ul className="list-disc space-y-1 pl-4">
                    {fieldValue.map((item, index) => (
                      <li key={index}>
                        {typeof item === "object"
                          ? JSON.stringify(item)
                          : String(item)}
                      </li>
                    ))}
                  </ul>
                )
              ) : (
                <pre className="overflow-x-auto rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
                  {JSON.stringify(fieldValue, null, 2)}
                </pre>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

const SummaryBlock = ({ summary }) => {
  const parsed = parseSummary(summary);
  if (parsed.type === "empty") {
    return <p className="text-sm text-gray-400">No summary</p>;
  }
  if (parsed.type === "text") {
    return (
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
        {parsed.value}
      </p>
    );
  }
  if (parsed.type === "takeaways") {
    return (
      <div className="space-y-4">
        {parsed.rawPrefix && (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
            {parsed.rawPrefix}
          </p>
        )}
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Key takeaways
          </p>
          <TakeawaysList takeaways={parsed.value} />
        </div>
        {parsed.extras && <JsonFields value={parsed.extras} />}
      </div>
    );
  }

  return <JsonFields value={parsed.value} />;
};

const CallRecordingCard = ({ recording, defaultTranscriptOpen = false }) => {
  const [transcriptOpen, setTranscriptOpen] = useState(defaultTranscriptOpen);
  const fields = recording.fields || {};
  // Participants is a comma-joined string that often repeats the same attendee.
  const participants = [
    ...new Set(
      String(fields.Participants || "")
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
    ),
  ];

  useEffect(() => {
    if (defaultTranscriptOpen) setTranscriptOpen(true);
  }, [defaultTranscriptOpen]);

  return (
    <article
      id={`call-recording-${recording.id}`}
      className="scroll-mt-24 rounded-xl border border-gray-100 bg-white p-5 sm:p-6 min-w-0 transition-shadow"
    >
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 break-words">
            {fields.Title || "Untitled call"}
          </h4>
          <p className="mt-1 text-xs text-gray-500">{formatCallDate(fields.Date)}</p>
        </div>
        {fields.Source && (
          <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
            {fields.Source}
          </span>
        )}
      </div>

      <div className="mt-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Participants
        </p>
        {participants.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {participants.map((person) => (
              <span
                key={person}
                className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200"
              >
                {person}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-sm text-gray-400">No participants listed</p>
        )}
      </div>

      <div className="mt-5 border-t border-gray-100 pt-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Summary
        </p>
        <div className="mt-2">
          <SummaryBlock summary={fields.Summary} />
        </div>
      </div>

      <div className="mt-5 border-t border-gray-100 pt-4">
        <button
          type="button"
          data-call-transcript-toggle="true"
          onClick={() => setTranscriptOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-2 text-left"
        >
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Transcript
          </span>
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${
              transcriptOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {transcriptOpen && (
          <div
            data-call-transcript-panel
            className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-3"
          >
            {fields.Transcript ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {fields.Transcript}
              </p>
            ) : (
              <p className="text-sm text-gray-400">No transcript</p>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

/**
 * Call recordings browser for company profile — filter by CallRecordings.Company.
 */
const CompanyCallRecordingsSection = ({ company, className = "" }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [focusedRecordingId, setFocusedRecordingId] = useState(null);
  const [openTranscriptForFocus, setOpenTranscriptForFocus] = useState(false);

  const currentCompanyId = company?.id || "";
  const currentCompanyName = company?.fields?.Name || "";

  const loadRecordings = useCallback(async (companyKey = currentCompanyId) => {
    setLoading(true);
    setError(null);
    try {
      const looksLikeRecordId = typeof companyKey === "string" && companyKey.startsWith("rec");

      const [catalogRes, companyRes] = await Promise.all([
        fetch("/api/callRecordings?limit=500&includeTranscript=1"),
        looksLikeRecordId
          ? fetch(
              `/api/callRecordings?companyId=${encodeURIComponent(companyKey)}&includeTranscript=1`
            )
          : Promise.resolve(null),
      ]);

      const catalogData = await catalogRes.json().catch(() => ({}));
      if (!catalogRes.ok && catalogData.tableNotFound) {
        setError(catalogData.error || "CallRecordings table not found");
        setRecordings([]);
        return;
      }

      const byId = new Map();
      for (const record of catalogData.records || []) {
        byId.set(record.id, record);
      }

      if (companyRes) {
        const companyData = await companyRes.json().catch(() => ({}));
        if (companyRes.ok) {
          for (const record of companyData.records || []) {
            byId.set(record.id, record);
          }
        }
      }

      setRecordings([...byId.values()]);
    } catch (err) {
      console.error("[CompanyCallRecordingsSection] load failed:", err);
      setError("Failed to load call recordings");
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, [currentCompanyId]);

  useEffect(() => {
    loadRecordings(currentCompanyId);
  }, [loadRecordings, currentCompanyId]);

  // When the select changes to another Airtable company id, pull every matching row.
  useEffect(() => {
    if (!selectedCompany || !selectedCompany.startsWith("rec")) return;
    if (selectedCompany === currentCompanyId) return;
    loadRecordings(selectedCompany);
  }, [selectedCompany, currentCompanyId, loadRecordings]);

  const companyOptions = useMemo(() => {
    const map = new Map();

    for (const recording of recordings) {
      const value = companyValueFromFields(recording.fields);
      if (!value) continue;
      const label = companyLabelFromFields(recording.fields);
      if (!map.has(value)) {
        map.set(value, { value, label });
      }
    }

    if (currentCompanyId) {
      map.set(currentCompanyId, {
        value: currentCompanyId,
        label: currentCompanyName || currentCompanyId,
      });
    }

    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
  }, [recordings, currentCompanyId, currentCompanyName]);

  useEffect(() => {
    if (selectedCompany) {
      const stillValid = companyOptions.some((opt) => opt.value === selectedCompany);
      if (stillValid) return;
    }
    if (currentCompanyId && companyOptions.some((opt) => opt.value === currentCompanyId)) {
      setSelectedCompany(currentCompanyId);
      return;
    }
    if (currentCompanyName) {
      const byName = companyOptions.find(
        (opt) => opt.label.toLowerCase() === currentCompanyName.toLowerCase()
      );
      if (byName) {
        setSelectedCompany(byName.value);
        return;
      }
    }
    if (!selectedCompany && companyOptions[0]) {
      setSelectedCompany(companyOptions[0].value);
    }
  }, [companyOptions, currentCompanyId, currentCompanyName, selectedCompany]);

  const selectedOption = companyOptions.find((opt) => opt.value === selectedCompany);

  const filteredRecordings = useMemo(() => {
    if (!selectedCompany) return [];
    return recordings
      .filter((recording) =>
        recordingMatchesSelection(
          recording.fields,
          selectedCompany,
          selectedOption?.label
        )
      )
      .sort((a, b) => {
        const aDate = new Date(a.fields?.Date || 0).getTime();
        const bDate = new Date(b.fields?.Date || 0).getTime();
        return bDate - aDate;
      });
  }, [recordings, selectedCompany, selectedOption?.label]);

  // Focus from company profile search palette (⌘⇧K)
  useEffect(() => {
    const applyFocus = (detail) => {
      if (!detail?.recordingId) return;
      setFocusedRecordingId(detail.recordingId);
      setOpenTranscriptForFocus(Boolean(detail.openTranscript));
      if (currentCompanyId) {
        setSelectedCompany(currentCompanyId);
      }
      window.setTimeout(() => {
        focusCompanyProfileCallRecording(detail.recordingId, {
          openTranscript: Boolean(detail.openTranscript),
        });
      }, 200);
      window.setTimeout(() => {
        focusCompanyProfileCallRecording(detail.recordingId, {
          openTranscript: Boolean(detail.openTranscript),
        });
      }, 700);
    };

    applyFocus(consumePendingCompanyProfileCallFocus());

    const handleFocus = (event) => {
      applyFocus(event?.detail);
      consumePendingCompanyProfileCallFocus();
    };

    window.addEventListener(COMPANY_PROFILE_CALL_FOCUS_EVENT, handleFocus);
    return () =>
      window.removeEventListener(COMPANY_PROFILE_CALL_FOCUS_EVENT, handleFocus);
  }, [currentCompanyId]);

  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold leading-7 text-gray-900">
            Call Recordings
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Browse matched calls by company from the CallRecordings table.
          </p>
        </div>
        <div className="w-full sm:max-w-xs">
          <label
            htmlFor="call-recordings-company"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Company
          </label>
          <select
            id="call-recordings-company"
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={loading || companyOptions.length === 0}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {companyOptions.length === 0 ? (
              <option value="">No companies found</option>
            ) : (
              companyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {loading && (
        <div className="mt-6 space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="animate-pulse space-y-3 rounded-xl border border-gray-100 p-6"
            >
              <div className="h-4 w-1/3 rounded bg-gray-100" />
              <div className="h-3 w-2/3 rounded bg-gray-100" />
              <div className="h-20 w-full rounded bg-gray-100" />
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="mt-6 rounded-xl border border-red-100 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!loading && !error && filteredRecordings.length === 0 && (
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-gray-100 bg-white py-12 text-center">
          <div className="mb-4 rounded-full bg-gray-100 p-3">
            <PhoneIcon className="h-6 w-6 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-900">No call recordings</p>
          <p className="mt-1 text-sm text-gray-500">
            {selectedCompany
              ? "No calls match this company yet."
              : "Select a company to view its calls."}
          </p>
        </div>
      )}

      {!loading && !error && filteredRecordings.length > 0 && (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-gray-500">
            {filteredRecordings.length} call
            {filteredRecordings.length === 1 ? "" : "s"}
            {selectedOption?.label ? ` for ${selectedOption.label}` : ""}
          </p>
          {filteredRecordings.map((recording) => (
            <CallRecordingCard
              key={recording.id}
              recording={recording}
              defaultTranscriptOpen={
                focusedRecordingId === recording.id && openTranscriptForFocus
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyCallRecordingsSection;
